/* The Motherboard module's bookkeeping: augment slots by tier, building augments from scrap,
   installing them, and selling scrap for Quantum. Each change returns a new character. */

import { Augment, AUGMENTS, SCRAP } from './content/motherboard';
import { levelOf, tierOf } from './leveling';
import { Character, Ikonis } from './model';

const SCRAP_ITEMS = SCRAP.flatMap((g) => g.items);

/** What a scrap sells for: the number printed before its name. Relics have none. */
export const scrapValue = (name: string) => SCRAP_ITEMS.find((s) => s.name === name)?.value ?? 0;

const NAMES = [...SCRAP_ITEMS.map((s) => s.name), 'Relic'];
const scrapName = (word: string) =>
  NAMES.find((n) => [n, `${n}s`, `${n}es`, n.replace(/y$/, 'ies')].map((w) => w.toLowerCase()).includes(word.toLowerCase()));

/** The scrap an augment takes, read from its printed cost ("3 gears, 2 lenses, ..."). */
export function augmentCost(a: Augment): Record<string, number> {
  const cost: Record<string, number> = {};
  Array.from(a.cost.matchAll(/(\d+)\s+([a-z]+)/gi)).forEach(([, n, word]) => {
    const name = scrapName(word);
    if (name) cost[name] = (cost[name] ?? 0) + Number(n);
  });
  return cost;
}

/** Two slots at Tier 1, one more at each tier. */
export const augmentSlots = (c: Character) => 1 + tierOf(levelOf(c));

const update = (c: Character, patch: Partial<Ikonis>): Character => ({ ...c, ikonis: { ...c.ikonis, ...patch } });

const have = (c: Character, name: string) => (name === 'Relic' ? c.ikonis.relics.length : c.ikonis.scrap[name] ?? 0);

export function canBuild(c: Character, a: Augment) {
  return (c.ikonis.built[a.name] ?? 0) < a.boxes && Object.entries(augmentCost(a)).every(([name, n]) => have(c, name) >= n);
}

/** Builds an augment, taking its scrap (relics from the end of the list); unchanged when it cannot be built. */
export function build(c: Character, a: Augment): Character {
  if (!canBuild(c, a)) return c;
  const { Relic: relics = 0, ...cost } = augmentCost(a);
  const scrap = { ...c.ikonis.scrap };
  Object.entries(cost).forEach(([name, n]) => { scrap[name] = (scrap[name] ?? 0) - n; });
  return update(c, {
    scrap,
    relics: c.ikonis.relics.slice(0, c.ikonis.relics.length - relics),
    built: { ...c.ikonis.built, [a.name]: (c.ikonis.built[a.name] ?? 0) + 1 },
  });
}

/** Takes one built augment apart again, giving its scrap back (relics come back unnamed) and uninstalling it if needed. */
export function unbuild(c: Character, a: Augment): Character {
  const built = c.ikonis.built[a.name] ?? 0;
  if (!built) return c;
  const { Relic: relics = 0, ...cost } = augmentCost(a);
  const scrap = { ...c.ikonis.scrap };
  Object.entries(cost).forEach(([name, n]) => { scrap[name] = (scrap[name] ?? 0) + n; });
  const installed = installedCount(c, a.name) >= built ? removeOne(c.ikonis.installed, a.name) : c.ikonis.installed;
  return update(c, { scrap, installed, relics: [...c.ikonis.relics, ...Array(relics).fill('Relic')], built: { ...c.ikonis.built, [a.name]: built - 1 } });
}

export const installedCount = (c: Character, name: string) => c.ikonis.installed.filter((n) => n === name).length;

const removeOne = (list: string[], name: string) => {
  const i = list.lastIndexOf(name);
  return i < 0 ? list : [...list.slice(0, i), ...list.slice(i + 1)];
};

/** Installs one more built copy of an augment, while a slot is free. */
export function install(c: Character, name: string): Character {
  const free = c.ikonis.installed.length < augmentSlots(c);
  return free && installedCount(c, name) < (c.ikonis.built[name] ?? 0) ? update(c, { installed: [...c.ikonis.installed, name] }) : c;
}

export const uninstall = (c: Character, name: string): Character => update(c, { installed: removeOne(c.ikonis.installed, name) });

export const setScrap = (c: Character, name: string, n: number): Character => update(c, { scrap: { ...c.ikonis.scrap, [name]: Math.max(0, n) } });

/** Sells one scrap for its value in Quantum. */
export function sell(c: Character, name: string): Character {
  const count = c.ikonis.scrap[name] ?? 0;
  return count ? update(c, { scrap: { ...c.ikonis.scrap, [name]: count - 1 }, quantum: c.ikonis.quantum + scrapValue(name) }) : c;
}

export const setQuantum = (c: Character, quantum: number): Character => update(c, { quantum: Math.max(0, quantum) });

/** The augments in the sheet's order with what this character has of each. */
export const augmentList = (c: Character) =>
  AUGMENTS.map((a) => ({ ...a, built: c.ikonis.built[a.name] ?? 0, installed: installedCount(c, a.name), buildable: canBuild(c, a) }));
